// ===================================
// TRENCH QUEST - Image Cache Service
// Persistent caching for AI-generated images using IndexedDB
// ===================================

import type { GeneratedImage } from '@/types'

const DB_NAME = 'trench-quest-images'
const DB_VERSION = 1
const STORE_NAME = 'images'
const MAX_CACHE_SIZE = 50 // Maximum number of images to cache
const DEFAULT_EXPIRY_MS = 24 * 60 * 60 * 1000 // 24 hours

/**
 * Open the IndexedDB database
 */
function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => {
      console.error('Failed to open image cache database:', request.error)
      reject(request.error)
    }

    request.onsuccess = () => {
      resolve(request.result)
    }

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result

      // Create the images store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' })
        store.createIndex('cacheKey', 'cacheKey', { unique: true })
        store.createIndex('createdAt', 'createdAt', { unique: false })
        store.createIndex('expiresAt', 'expiresAt', { unique: false })
      }
    }
  })
}

/**
 * Create a cache key from visual tags and style
 */
export function createCacheKey(visualTags: string[], style: string = 'pixel_art'): string {
  const sortedTags = [...visualTags].sort().join('|')
  return `${style}:${sortedTags}`
}

/**
 * Hash visual tags for shorter cache keys
 */
export function hashVisualTags(visualTags: string[]): string {
  const str = visualTags.sort().join('|')
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36)
}

/**
 * Image Cache Service for managing generated scene images
 */
export class ImageCacheService {
  private static instance: ImageCacheService
  private db: IDBDatabase | null = null
  private initPromise: Promise<void> | null = null

  private constructor() {}

  static getInstance(): ImageCacheService {
    if (!ImageCacheService.instance) {
      ImageCacheService.instance = new ImageCacheService()
    }
    return ImageCacheService.instance
  }

  /**
   * Initialize the database connection
   */
  async init(): Promise<void> {
    if (this.db) return

    if (this.initPromise) {
      return this.initPromise
    }

    this.initPromise = (async () => {
      try {
        this.db = await openDatabase()
      } catch (error) {
        console.error('Failed to initialize image cache:', error)
        // Fall back to memory-only cache
        this.db = null
      }
    })()

    return this.initPromise
  }

  /**
   * Get an image from the cache by cache key
   */
  async getImage(cacheKey: string): Promise<GeneratedImage | null> {
    await this.init()

    if (!this.db) {
      return null
    }

    return new Promise((resolve) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const index = store.index('cacheKey')
      const request = index.get(cacheKey)

      request.onerror = () => {
        console.error('Failed to get cached image:', request.error)
        resolve(null)
      }

      request.onsuccess = () => {
        const result = request.result as (GeneratedImage & { cacheKey: string }) | undefined

        if (!result) {
          resolve(null)
          return
        }

        // Check if expired
        if (result.expiresAt && new Date(result.expiresAt) < new Date()) {
          // Delete expired image
          this.deleteImage(result.id).catch(console.error)
          resolve(null)
          return
        }

        resolve(result)
      }
    })
  }

  /**
   * Get an image by situation ID
   */
  async getImageBySituationId(situationId: string): Promise<GeneratedImage | null> {
    await this.init()

    if (!this.db) {
      return null
    }

    return new Promise((resolve) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.openCursor()

      request.onerror = () => {
        resolve(null)
      }

      request.onsuccess = () => {
        const cursor = request.result
        if (cursor) {
          const image = cursor.value as GeneratedImage
          if (image.situationId === situationId) {
            // Check if expired
            if (image.expiresAt && new Date(image.expiresAt) < new Date()) {
              this.deleteImage(image.id).catch(console.error)
              cursor.continue()
            } else {
              resolve(image)
            }
          } else {
            cursor.continue()
          }
        } else {
          resolve(null)
        }
      }
    })
  }

  /**
   * Save an image to the cache
   */
  async saveImage(
    image: GeneratedImage,
    cacheKey: string
  ): Promise<void> {
    await this.init()

    if (!this.db) {
      return
    }

    // Ensure cache doesn't grow too large
    await this.enforceMaxSize()

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)

      const imageWithKey = {
        ...image,
        cacheKey,
        expiresAt: image.expiresAt || new Date(Date.now() + DEFAULT_EXPIRY_MS),
      }

      const request = store.put(imageWithKey)

      request.onerror = () => {
        console.error('Failed to save image to cache:', request.error)
        reject(request.error)
      }

      request.onsuccess = () => {
        resolve()
      }
    })
  }

  /**
   * Delete an image from the cache
   */
  async deleteImage(imageId: string): Promise<void> {
    await this.init()

    if (!this.db) {
      return
    }

    return new Promise((resolve) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.delete(imageId)

      request.onerror = () => {
        console.error('Failed to delete cached image:', request.error)
        resolve()
      }

      request.onsuccess = () => {
        resolve()
      }
    })
  }

  /**
   * Clear all cached images
   */
  async clearCache(): Promise<void> {
    await this.init()

    if (!this.db) {
      return
    }

    return new Promise((resolve) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.clear()

      request.onerror = () => {
        console.error('Failed to clear image cache:', request.error)
        resolve()
      }

      request.onsuccess = () => {
        resolve()
      }
    })
  }

  /**
   * Remove expired images from the cache
   */
  async cleanupExpired(): Promise<number> {
    await this.init()

    if (!this.db) {
      return 0
    }

    return new Promise((resolve) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const index = store.index('expiresAt')
      const now = new Date()
      let deletedCount = 0

      const request = index.openCursor(IDBKeyRange.upperBound(now))

      request.onerror = () => {
        console.error('Failed to cleanup expired images:', request.error)
        resolve(deletedCount)
      }

      request.onsuccess = () => {
        const cursor = request.result
        if (cursor) {
          cursor.delete()
          deletedCount++
          cursor.continue()
        } else {
          resolve(deletedCount)
        }
      }
    })
  }

  /**
   * Enforce maximum cache size by removing oldest entries
   */
  private async enforceMaxSize(): Promise<void> {
    await this.init()

    if (!this.db) {
      return
    }

    return new Promise((resolve) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const countRequest = store.count()

      countRequest.onsuccess = () => {
        const count = countRequest.result
        if (count >= MAX_CACHE_SIZE) {
          // Delete oldest entries
          const index = store.index('createdAt')
          const deleteCount = count - MAX_CACHE_SIZE + 10 // Delete extra to avoid frequent cleanup
          let deleted = 0

          const cursorRequest = index.openCursor()
          cursorRequest.onsuccess = () => {
            const cursor = cursorRequest.result
            if (cursor && deleted < deleteCount) {
              cursor.delete()
              deleted++
              cursor.continue()
            } else {
              resolve()
            }
          }

          cursorRequest.onerror = () => {
            resolve()
          }
        } else {
          resolve()
        }
      }

      countRequest.onerror = () => {
        resolve()
      }
    })
  }

  /**
   * Get cache statistics
   */
  async getStats(): Promise<{
    count: number
    oldestEntry: Date | null
    newestEntry: Date | null
  }> {
    await this.init()

    if (!this.db) {
      return { count: 0, oldestEntry: null, newestEntry: null }
    }

    return new Promise((resolve) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const index = store.index('createdAt')

      let count = 0
      let oldestEntry: Date | null = null
      let newestEntry: Date | null = null

      const countRequest = store.count()
      countRequest.onsuccess = () => {
        count = countRequest.result
      }

      const oldestRequest = index.openCursor()
      oldestRequest.onsuccess = () => {
        const cursor = oldestRequest.result
        if (cursor) {
          oldestEntry = new Date(cursor.value.createdAt)
        }
      }

      const newestRequest = index.openCursor(null, 'prev')
      newestRequest.onsuccess = () => {
        const cursor = newestRequest.result
        if (cursor) {
          newestEntry = new Date(cursor.value.createdAt)
        }
      }

      transaction.oncomplete = () => {
        resolve({ count, oldestEntry, newestEntry })
      }

      transaction.onerror = () => {
        resolve({ count: 0, oldestEntry: null, newestEntry: null })
      }
    })
  }

  /**
   * Check if a similar image exists in cache
   * Uses visual tag similarity for fuzzy matching
   */
  async findSimilarImage(
    visualTags: string[],
    minSimilarity: number = 0.7
  ): Promise<GeneratedImage | null> {
    await this.init()

    if (!this.db) {
      return null
    }

    return new Promise((resolve) => {
      const transaction = this.db!.transaction(STORE_NAME, 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.openCursor()

      let bestMatch: GeneratedImage | null = null
      let bestSimilarity = 0

      request.onerror = () => {
        resolve(bestMatch)
      }

      request.onsuccess = () => {
        const cursor = request.result
        if (cursor) {
          const image = cursor.value as GeneratedImage

          // Skip expired images
          if (image.expiresAt && new Date(image.expiresAt) < new Date()) {
            cursor.continue()
            return
          }

          // Calculate Jaccard similarity between tag sets
          const tagSet1 = new Set(visualTags)
          const tagSet2 = new Set(image.visualTags)
          const intersection = new Set([...tagSet1].filter(x => tagSet2.has(x)))
          const union = new Set([...tagSet1, ...tagSet2])
          const similarity = intersection.size / union.size

          if (similarity >= minSimilarity && similarity > bestSimilarity) {
            bestSimilarity = similarity
            bestMatch = image
          }

          cursor.continue()
        } else {
          resolve(bestMatch)
        }
      }
    })
  }
}

// Export singleton instance
export const imageCache = ImageCacheService.getInstance()

/**
 * Helper to preload and cache an image URL
 */
export async function preloadImage(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve()
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`))
    img.src = url
  })
}

/**
 * Convert a blob URL to a data URL for persistence
 */
export async function blobToDataUrl(blobUrl: string): Promise<string> {
  const response = await fetch(blobUrl)
  const blob = await response.blob()

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result as string)
    reader.onerror = () => reject(new Error('Failed to convert blob to data URL'))
    reader.readAsDataURL(blob)
  })
}
